import bs58 from 'bs58';
import {
  AUTH_MESSAGE_VERSION,
  buildAuthMessage,
  randomNonceHex,
  type AuthAction,
} from '@avocado-solana/shared';
import { env } from '@/lib/env';
import { useWalletStore } from '@/stores/wallet';
import { useMultisigStore } from '@/stores/multisig';

export interface SignedFetchOptions {
  /** Override the multisig PDA. Defaults to active multisig from the store; '-' if no active multisig (for `multisig.finalize`). */
  multisigPda?: string;
  /** Action data fields included after the header (e.g. `{ 'Code': '123456' }`). */
  fields?: Record<string, string | number>;
  /** Time-to-live in seconds. Default 60s. Backend caps at 5 minutes. */
  ttlSec?: number;
  /** Override the route. Defaults to `/${action.replaceAll('.', '/')}`. */
  path?: string;
}

export interface SignedFetchResult<T> {
  ok: boolean;
  status: number;
  data: T;
}

const ACTION_PATHS: Partial<Record<AuthAction, string>> = {
  'multisig.finalize': '/multisig/finalize',
  'mfa.totp.setup': '/mfa/totp/setup',
  'mfa.totp.verify': '/mfa/totp/verify',
  'mfa.totp.recovery.regenerate': '/mfa/totp/recovery/regenerate',
  'mfa.cleanup': '/mfa/cleanup',
  'session.create': '/sessions',
  'session.list': '/sessions/list',
  'session.revoke': '/sessions/revoke',
  'transaction.sign': '/sign',
};

/** Build, sign, and POST a `Squads-MS Authorization v1` request. */
export async function signedFetch<T = unknown>(
  action: AuthAction,
  opts: SignedFetchOptions = {},
): Promise<SignedFetchResult<T>> {
  const wallet = useWalletStore.getState();
  const ms = useMultisigStore.getState();

  const pk = wallet.publicKey();
  if (!pk) {
    throw new Error('connect wallet before calling signedFetch');
  }
  const ownerPubkey = pk.toBase58();

  let multisigPda: string;
  if (opts.multisigPda !== undefined) {
    multisigPda = opts.multisigPda;
  } else if (action === 'multisig.finalize') {
    multisigPda = '-';
  } else {
    if (!ms.activePda) throw new Error('no active multisig selected');
    multisigPda = ms.activePda;
  }

  const ttl = opts.ttlSec ?? 60;
  const issuedAt = new Date();
  const expiresAt = new Date(issuedAt.getTime() + ttl * 1000);

  const fieldsStr: Record<string, string | number> = {};
  if (opts.fields) {
    for (const [k, v] of Object.entries(opts.fields)) fieldsStr[k] = v;
  }

  const message = buildAuthMessage({
    domain: env.appDomain,
    ownerPubkey,
    action,
    multisigPda: multisigPda === '-' ? null : multisigPda,
    cluster: env.solanaCluster,
    issuedAt,
    expiresAt,
    nonce: randomNonceHex(),
    fields: fieldsStr,
  });

  // wallet.signMessage now guarantees a plain 64-byte Uint8Array (the wallet
  // store does the cross-realm + cross-shape normalisation in one place).
  const sigBytes = await wallet.signMessage(new TextEncoder().encode(message));
  const signature = bs58.encode(sigBytes);

  const path = opts.path ?? ACTION_PATHS[action] ?? '/' + action.split('.').join('/');
  const url = env.apiBase.replace(/\/$/, '') + path;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ message, signature }),
  });

  let data: T;
  try {
    data = (await res.json()) as T;
  } catch {
    data = null as unknown as T;
  }
  return { ok: res.ok, status: res.status, data };
}

/** Backwards-compatible shorthand the rest of the app may use. */
export function useSignedFetch() {
  return { signedFetch, version: AUTH_MESSAGE_VERSION };
}
